import { Box, Button } from '@mui/material';
import { PostSkeleton } from '@client/new/community/components/PostSkeleton';
import { Fragment, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { CommunityPostCell } from '@client/new/community/components/CommunityPostCell';
import { useCommunityPost } from '@client/new/community/hooks/useCommunityPost';
import { CommunityReplyCell } from '@client/new/community/components/CommunityReplyCell';
import {
  Reply,
  useBlockUserMutation,
  useGetCommunityQuery,
} from '../../../generated/graphql';
import { ReplySkeleton } from '@client/new/community/components/ReplySkeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faMessages } from '@fortawesome/pro-regular-svg-icons';
import { AddReply } from '@client/new/community/components/AddReply';
import { useAddReplyMutation } from '@client/new/community/hooks/useAddReply';
import { useCommunityComments } from './hooks/useCommunityComments';
import { useDeleteReplyMutation } from '@client/new/community/hooks/useDeleteReply';
import { useDeleteCommunityPost } from '@client/new/community/hooks/useDeleteCommunityPost';
import { navigate, PATHS } from '@client/routes';
import auth from '@client/components/auth';
import { getReplyInDMHandler } from './utils/replyInDM';
import { useToggleReactionPost } from './hooks/useToggleReactionPost';
import { useToggleReactionReply } from './hooks/useToggleReactionReply';
import { useTogglePinPost } from './hooks/useTogglePinPost';
import { usePinnedPosts } from './hooks/usePinnedPosts';
import { MAX_PINNED_POSTS } from './constants';
import { CommunityLayout } from './components/CommunityLayout';
import toast from 'react-hot-toast';
import { communityQueryKeys } from './constants';
import { useQueryClient } from '@tanstack/react-query';
import { mixTrack } from '@services/mixpanel';
import { UseEffectOnTrue } from './components/UseEffectOnTrue';
import { useDmFeatureEnabled } from './hooks/useDmFeatureEnabled';

type PostPageProps = {
  params: {
    influencerId: string;
    communityId: string;
    postId: string;
  };
  location: {
    hash: string;
  };
};

/**
 *
 * @param {PostPageProps} props
 * @return {JSX.Element}
 * @constructor
 */
export const PostPage = (props: PostPageProps): JSX.Element => {
  const influencerId = parseInt(props.params.influencerId);
  const queryClient = useQueryClient();
  const { isDmFeatureEnabled } = useDmFeatureEnabled(influencerId);
  const {
    params: { communityId, postId },
    location: { hash },
  } = props;

  const [postComments, setPostComments] = useState<Reply[]>([]);
  const [hasNewComments, setHasNewComments] = useState<boolean>(false);

  const addReplyRef = useRef<HTMLTextAreaElement>(null);

  const { data: post, isInitialLoading: isPostInitialLoading } =
    useCommunityPost(Number(communityId), postId);
  const { data: communityData } = useGetCommunityQuery({
    variables: { id: +communityId },
  });
  const ownerName = communityData?.community?.owner.username || 'Creator';
  const isUserCommunityOwner =
    communityData?.community?.owner.badges.includes('own') || false;

  const lastReplyId = hash ? hash.substring(1) : undefined;

  const {
    data: comments,
    isFetchingNextPage,
    fetchNextPage,
    isInitialLoading: areCommentsInitialLoading,
    hasNextPage,
  } = useCommunityComments(Number(communityId), postId, lastReplyId);

  useEffect(() => {
    const replies = _.reverse(_.flatten(comments?.pages ?? []));
    if (
      !_.isEmpty(postComments) &&
      !_.isEmpty(replies) &&
      replies.length > postComments.length
    ) {
      setHasNewComments(true);
    }
    if (_.isEmpty(postComments)) {
      setPostComments(replies as Reply[]);
    }
  }, [comments]);

  const addReply = useAddReplyMutation(Number(communityId), postId);
  const deleteReply = useDeleteReplyMutation(Number(communityId), postId);
  const deletePost = useDeleteCommunityPost(Number(communityId));
  const togglePostReaction = useToggleReactionPost(communityId);
  const toggleReplyReaction = useToggleReactionReply(communityId);
  const togglePinPost = useTogglePinPost(Number(communityId));
  const { data: pinnedPosts } = usePinnedPosts(Number(communityId));
  const [blockUserMutation] = useBlockUserMutation();

  const pinnedPostIds = new Set(pinnedPosts?.map((p) => p.id) ?? []);
  const pinCount = pinnedPosts?.length ?? 0;
  const pinLimitReached = pinCount >= MAX_PINNED_POSTS;

  const onBlock = async (communityId: number, userId: number) => {
    return blockUserMutation({
      variables: {
        communityId,
        userId,
      },
    })
      .then(() => {
        toast.success('User blocked successfully');
        queryClient.refetchQueries(communityQueryKeys.posts(+communityId));
        navigate(PATHS.COMMUNITY(influencerId, communityId));
      })
      .catch(() => {
        toast.error('Something went wrong with blocking user');
      });
  };

  const handleAddComment = () => {
    if (!addReplyRef.current) return;

    addReplyRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    addReplyRef.current.focus({ preventScroll: true });
  };

  const handleDeletePost = async () => {
    mixTrack({
      type: 'Post Deleted',
      payload: {
        'Media Type': post?.media
          .map((m) => (m.video ? 'video' : 'image'))
          .join(', '),
        'Post Owner': post?.author.id === influencerId ? 'Creator' : 'Consumer',
      },
    });

    await deletePost.mutate(postId);
    navigate(PATHS.COMMUNITY(influencerId, communityId));
  };

  /** @const {Reply[]} replies - All replies from all the pages, sorted in reverse chronological order */

  const isInitialLoading = isPostInitialLoading || areCommentsInitialLoading;

  const onLoadNewComments = () => {
    const replies = _.reverse(_.flatten(comments?.pages ?? []));
    setPostComments(replies as Reply[]);
    if (!_.isEmpty(replies)) {
      const element = document.getElementById(
        replies[replies.length - 1]?.id || '',
      );
      element?.scrollIntoView({ behavior: 'smooth' });
    }
    setHasNewComments(false);
  };

  return (
    <CommunityLayout
      communityId={+communityId}
      postId={postId}
      ownerName={ownerName}
      influencerId={influencerId}
    >
      <Box className="h-[85vh] min-h-[200px] max-w-[680px] my-0 mx-auto flex flex-col justify-between ">
        {isInitialLoading && (
          <Fragment>
            <PostSkeleton />
            <ReplySkeleton />
            <ReplySkeleton />
          </Fragment>
        )}
        {!isInitialLoading && (
          <Box className="h-full flex flex-col bg-white rounded-2xl md:pb-[20px] pt-2.5 px-2.5">
            {post ? (
              <CommunityPostCell
                influencerId={influencerId}
                communityId={Number(communityId)}
                isUserCommunityOwner={isUserCommunityOwner}
                postData={post}
                onAddComment={handleAddComment}
                onDelete={handleDeletePost}
                onBlock={onBlock}
                onToggleReaction={(type) => {
                  togglePostReaction.mutateAsync({ postId: post.id, type });
                  mixTrack({
                    type: 'Reaction Added',
                    payload: {
                      'Media Type': post.media
                        .map((m) => (m.video ? 'video' : 'image'))
                        .join(', '),
                      'Post Type':
                        post.author.id === influencerId
                          ? 'Creator'
                          : 'Consumer',
                      'Reaction Location': 'Post',
                    },
                  });
                }}
                isPostPage={true}
                onReplyInDM={getReplyInDMHandler({
                  author: post.author,
                  isOwner: isUserCommunityOwner,
                  isAdminModerator: auth.isAdministrator(),
                  influencerId,
                  isInfluencer: auth.isInfluencer(),
                  isDmFeatureEnabled,
                })}
                isPinned={pinnedPostIds.has(post.id)}
                onPin={() =>
                  togglePinPost.mutate({
                    postId: post.id,
                    pinned: !pinnedPostIds.has(post.id),
                    post: post as any,
                  })
                }
                pinLimitReached={pinLimitReached}
              />
            ) : (
              <PostSkeleton />
            )}
            {hasNextPage && !isFetchingNextPage && (
              <Button
                variant="text"
                className={'text-gray-2 m-4'}
                onClick={() => {
                  fetchNextPage();
                  mixTrack({
                    type: 'View Previous Comments Tapped',
                    payload: {},
                  });
                }}
              >
                <FontAwesomeIcon className="mr-3" icon={faMessages} />
                View previous comments
              </Button>
            )}
            <Box className="px-5 grow overflow-y-scroll no-scrollbar">
              {isFetchingNextPage && <ReplySkeleton />}
              {postComments.map((reply) => {
                if (reply && post) {
                  return (
                    <CommunityReplyCell
                      key={reply.id}
                      reply={reply as Reply}
                      communityId={Number(communityId)}
                      postId={postId}
                      isUserCommunityOwner={isUserCommunityOwner}
                      onDelete={() => {
                        deleteReply.mutate(reply.id);
                        mixTrack({
                          type: 'Comment Deleted',
                          payload: {
                            'Comment Owner':
                              reply.author.id === influencerId
                                ? 'Creator'
                                : 'Consumer',
                            'Media Type': 'text',
                          },
                        });
                      }}
                      onBlock={onBlock}
                      onToggleReaction={(type) =>
                        toggleReplyReaction.mutateAsync({
                          postId: post.id,
                          replyId: reply.id,
                          type,
                        })
                      }
                      userEmail={reply.author.email}
                      onReplyInDM={getReplyInDMHandler({
                        author: reply.author,
                        isOwner: isUserCommunityOwner,
                        isAdminModerator: auth.isAdministrator(),
                        influencerId,
                        isInfluencer: auth.isInfluencer(),
                        isDmFeatureEnabled,
                      })}
                    />
                  );
                }

                return null;
              })}
              {addReply.isLoading && <ReplySkeleton />}
            </Box>
            <Box className="px-5 relative">
              {hasNewComments && (
                <Button
                  variant="contained"
                  className="absolute rounded-[100px] w-[175px] h-[28px] top-[-40px] left-0 right-0 m-auto"
                  startIcon={
                    <FontAwesomeIcon
                      icon={faArrowDown}
                      className="pl-2.5 h-4"
                    />
                  }
                  onClick={onLoadNewComments}
                >
                  <span className="px-2">New Comment</span>
                </Button>
              )}
              <UseEffectOnTrue
                flag={hasNewComments}
                action={onLoadNewComments}
                delayMs={1000}
              />
              <AddReply
                inputRef={addReplyRef}
                submit={(text: string) => addReply.mutate({ text })}
              />
            </Box>
          </Box>
        )}
      </Box>
    </CommunityLayout>
  );
};
